import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { useKeycloak } from "@react-keycloak/web";
import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import BusinessHoursSection from "../components/companyForm/BusinessHoursSection";
import CompanyDetailsSection from "../components/companyForm/CompanyDetailsSection";
import CompanyInformationToolbar from "../components/companyForm/CompanyInformationToolbar";
import ContactDetailsSection from "../components/companyForm/ContactDetailsSection";
import DMORegionSection from "../components/companyForm/DMORegionSection";
import ExternalLinksSection from "../components/companyForm/ExternalLinksSection";
import LocationDetailsSection from "../components/companyForm/LocationDetailsSection";
import SaveCompanyModal from "../components/companyForm/SaveCompanyModal";
import { useUpdateSocialMedias } from "../components/hooks/useUpdateSocialMedias";
import Notification from "../components/Notification";
import PageHeader from "../components/PageHeader";
import SocialMediaSection from "../components/productForm/SociaMediaSection";
import deleteBusinessHoursMutation, {
  DeleteBusinessHoursResult,
  DeleteBusinessHoursVariables,
} from "../graphqlQueries/deleteBusinessHours";
import getCuratorAreas, { CuratorAreasResult } from "../graphqlQueries/getCuratorAreas";
import getCompanyData, {
  GetSingleCompanyInfoResult,
  GetSingleCompanyInfoVariables,
} from "../graphqlQueries/getSingleCompanyInfo";
import insertBusinessHoursMutation, {
  InsertBusinessHoursResult,
  InsertBusinessHoursVariables,
} from "../graphqlQueries/insertBusinessHours";
import updateCompanyInfoMutation, {
  UpdateCompanyInfoResult,
  UpdateCompanyInfoVariables,
} from "../graphqlQueries/updateCompanyInfo";
import updateOpeningHoursMutation, {
  UpdateOpeningHoursResult,
  UpdateOpeningHoursVariables,
} from "../graphqlQueries/updateOpeningHours";
import { CompanyInformationValues, DatahubType, ProductSocialMediaLink, UserRole } from "../types";
import { getHasuraRoleContext, isVisitFinlandAdmin } from "../utils/functions";
import {
  mapOpeningHoursExceptionsToFormValues,
  mapOpeningHoursToWeek,
} from "../utils/mapDbProductToFormValues";
import { getUpdateOpeningHoursVariables } from "../utils/productFormUtils/mapProductToHasura";
import Page from "./Page";
import BackNavigationSideBar from "../components/BackNavigationSideBar";

type SaveResult = "noresult" | "ok" | "error";

type CompanyInformationProps = {
  companyId: string;
};

const manageCompanyContext = getHasuraRoleContext(UserRole.ManageCompany);

const CompanyInformation = ({ companyId }: CompanyInformationProps) => {
  const apollo = useApolloClient();
  const { t } = useTranslation();
  const { keycloak } = useKeycloak();
  const { updateSocialMedia } = useUpdateSocialMedias();
  const { state } = useLocation<{ previousPath: string | undefined }>();

  const canManageCuration = keycloak.hasResourceRole(UserRole.ManageCuration);
  const isVFAdmin = isVisitFinlandAdmin(keycloak);

  const hookFormMethods = useForm<CompanyInformationValues>();
  const { handleSubmit, reset, register } = hookFormMethods;
  const [saveResult, setSaveResult] = useState<SaveResult>("noresult");

  const [companyInfo, setCompanyInfo] = useState<CompanyInformationValues>();

  const { data: companyData, error: companyDataError, refetch: companyDataRefetch } = useQuery<
    GetSingleCompanyInfoResult,
    GetSingleCompanyInfoVariables
  >(getCompanyData, {
    context: manageCompanyContext,
    fetchPolicy: "cache-and-network",
    variables: {
      id: companyId,
    },
  });

  const companyIsDmo = companyData?.companyByPk.datahubType === DatahubType.DMO;
  const [updateCompanyInfo] = useMutation<UpdateCompanyInfoResult, UpdateCompanyInfoVariables>(
    updateCompanyInfoMutation
  );

  useQuery<CuratorAreasResult>(getCuratorAreas, {
    context: manageCompanyContext,
    variables: { id: companyId },
  });

  const onSubmit = async (values: CompanyInformationValues) => {
    const getBusinessHoursId = async () => {
      const { businessHoursEnabled, businessHoursId } = values;
      if (businessHoursEnabled) {
        if (businessHoursId) {
          return businessHoursId;
        }

        const result = await apollo.mutate<InsertBusinessHoursResult, InsertBusinessHoursVariables>(
          {
            context: manageCompanyContext,
            mutation: insertBusinessHoursMutation,
            variables: { companyId },
          }
        );

        return result.data?.insertBusinessHoursOne.id || null;
      }
      return null;
    };

    const updateBusinessHours = async (businessHoursId: string | null) => {
      if (businessHoursId) {
        const updateResult = await apollo.mutate<
          UpdateOpeningHoursResult,
          UpdateOpeningHoursVariables
        >({
          context: manageCompanyContext,
          mutation: updateOpeningHoursMutation,
          variables: getUpdateOpeningHoursVariables(values, businessHoursId),
        });
        return updateResult.data || null;
      } else if (values.businessHoursId) {
        const deleteResult = await apollo.mutate<
          DeleteBusinessHoursResult,
          DeleteBusinessHoursVariables
        >({
          context: manageCompanyContext,
          mutation: deleteBusinessHoursMutation,
          variables: { businessHoursId: values.businessHoursId },
        });
        return deleteResult.data || null;
      }
      return null;
    };

    const getDmoAreas = () => {
      if (!canManageCuration) {
        return [];
      }

      return (
        values.cities?.map((city) => ({
          companyId: values.companyUuid,
          curationAreaId: city.value,
        })) || []
      );
    };

    try {
      const logo = values.images?.[0];
      const socialMediaId = await updateSocialMedia({
        companyId,
        productSocialMediaLinks: values.socialMediaLinks ?? [],
        socialMediaId: companyData?.companyByPk?.socialMedia?.id,
        userRole: UserRole.ManageCompany,
      });

      const businessHoursId = await getBusinessHoursId();

      await updateCompanyInfo({
        context: manageCompanyContext,
        refetchQueries: canManageCuration ? [getCuratorAreas] : undefined,
        variables: {
          businessHoursId,
          businessName: values.businessName,
          city: values.city,
          completed: true,
          description: values.description,
          dmoAreas: getDmoAreas(),
          email: values.email,
          id: values.companyUuid,
          logoThumbnailUrl: logo?.thumbnailUrl || null,
          logoUrl: logo?.originalUrl || null,
          phone: values.phonenumber,
          postalCode: values.postalCode,
          socialMediaId: socialMediaId || null,
          streetName: values.streetName,
          webshopUrl: values.webshop,
          websiteUrl: values.website,
        },
      });

      await updateBusinessHours(businessHoursId);

      await companyDataRefetch();

      setSaveResult("ok");
    } catch (e) {
      setSaveResult("error");
      console.error(e);
    }
  };

  useEffect(() => {
    if (companyDataError) {
      console.error(companyDataError);
    }

    if (companyData) {
      const company = companyData.companyByPk;
      const linkData = company?.socialMedia?.socialMediaLinks || [];

      const socialMediaLinks: ProductSocialMediaLink[] = linkData.map(
        ({ linkType, verifiedLink }) => {
          return {
            linkType,
            verifiedLink,
          };
        }
      );

      const images = company.logoUrl
        ? [
            {
              filename: "",
              largeUrl: company.logoUrl,
              originalUrl: company.logoUrl,
              thumbnailUrl: company.logoThumbnailUrl,
            },
          ]
        : [];

      const cities = company.dmoAreas.map((dmoArea) => ({
        label: dmoArea.curationArea.city,
        value: dmoArea.curationArea.id,
      }));

      const businessHoursId = company.businessHours?.id ?? "";

      const newCompanyInfo: CompanyInformationValues = {
        businessHoursEnabled: !!businessHoursId,
        businessHoursId,
        businessId: company.businessEntity?.businessId,
        businessName: company.businessName,
        cities: cities,
        city: company.postalAddresses[0]?.city ?? "",
        companySocialMediaId: company.socialMedia?.id ?? "",
        companyUuid: company.id,
        description: company.description,
        email: company.contactDetails[0]?.email ?? "",
        images: images,
        officialName: company.officialName,
        openingHours: mapOpeningHoursToWeek(company.businessHours?.default),
        openingHoursExceptions: mapOpeningHoursExceptionsToFormValues(
          company.businessHours?.exceptions
        ),
        phonenumber: company.contactDetails[0]?.phone ?? "",
        postalCode: company.postalAddresses[0]?.postalCode ?? "",
        socialMediaLinks,
        streetName: company.postalAddresses[0]?.streetName ?? "",
        webshop: company.webshopUrl,
        website: company.websiteUrl,
      };
      reset(newCompanyInfo);
      setCompanyInfo(newCompanyInfo);
    }
  }, [companyData, companyDataError, reset]);

  return (
    <Page sideBar={<BackNavigationSideBar backPath={state?.previousPath || "/"} />}>
      <FormProvider {...hookFormMethods}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Form.Control type="hidden" name="companyUuid" ref={register()} />
          <CompanyInformationToolbar
            onReset={() => {
              reset(companyInfo);
            }}
          />
          {companyDataError && <Notification type="danger">{t("common.error")}</Notification>}
          <div className="p-3 p-lg-5 m-md-3 mx-lg-4">
            <PageHeader data-cy="company-information-page-header">
              {canManageCuration ? t("companyInfo.dmoHeader") : t("companyInfo.header")}
            </PageHeader>
            <p>
              {canManageCuration ? t("companyInfo.dmoDescription") : t("companyInfo.description")}
            </p>
            <CompanyDetailsSection isCurator={canManageCuration} />
            {canManageCuration && !isVFAdmin && companyIsDmo && <DMORegionSection />}
            <LocationDetailsSection isCurator={canManageCuration} />
            <BusinessHoursSection />
            <ContactDetailsSection isCurator={canManageCuration} />
            <ExternalLinksSection isCurator={canManageCuration} />
            <SocialMediaSection
              headerKey="companyInfo.socialMediaHeader"
              descriptionKey="companyInfo.socialMediaDescription"
              fieldName="socialMediaLinks"
              isCompanyForm={true}
            />
          </div>
        </Form>
        {saveResult !== "noresult" && (
          <SaveCompanyModal saveResult={saveResult} onHide={() => setSaveResult("noresult")} />
        )}
      </FormProvider>
    </Page>
  );
};

export default CompanyInformation;
